import axios from 'axios';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const inFlightGetRequests = new Map();
const cachedGetResponses = new Map();
const requestVersions = new Map();

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeValue(value[key]);
        return accumulator;
      }, {});
  }

  return value;
};

const cloneData = (value) => {
  if (value === undefined) return value;
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
};

const buildCacheKey = (url, config = {}) => {
  const params = config.params ? JSON.stringify(normalizeValue(config.params)) : '';
  return `get:${url}:${params}`;
};

const getRequestVersion = (key) => requestVersions.get(key) || 0;

const getCachedResponse = (key, config) => {
  const cachedEntry = cachedGetResponses.get(key);
  if (!cachedEntry) return null;

  if (cachedEntry.expiresAt <= Date.now()) {
    cachedGetResponses.delete(key);
    return null;
  }

  return {
    data: cloneData(cachedEntry.data),
    status: cachedEntry.status,
    statusText: cachedEntry.statusText,
    headers: cachedEntry.headers,
    config
  };
};

const invalidateCacheEntries = (matcher) => {
  const predicate = typeof matcher === 'function'
    ? matcher
    : (key) => key.startsWith(String(matcher));

  [...cachedGetResponses.keys()].forEach((key) => {
    if (predicate(key)) {
      requestVersions.set(key, getRequestVersion(key) + 1);
      cachedGetResponses.delete(key);
    }
  });

  [...inFlightGetRequests.keys()].forEach((key) => {
    if (predicate(key)) {
      requestVersions.set(key, getRequestVersion(key) + 1);
      inFlightGetRequests.delete(key);
    }
  });
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.setContentType === 'function') {
        config.headers.setContentType(undefined);
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      cachedGetResponses.clear();
      inFlightGetRequests.clear();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

api.cachedGet = async (url, config = {}, options = {}) => {
  const {
    cacheKey,
    ttlMs = 0,
    dedupe = true,
    force = false
  } = options;

  const resolvedKey = cacheKey || buildCacheKey(url, config);
  const requestVersion = getRequestVersion(resolvedKey);

  if (!force && ttlMs > 0) {
    const cachedResponse = getCachedResponse(resolvedKey, config);
    if (cachedResponse) {
      return cachedResponse;
    }
  }

  if (dedupe && inFlightGetRequests.has(resolvedKey)) {
    return inFlightGetRequests.get(resolvedKey);
  }

  const requestPromise = api.get(url, config)
    .then((response) => {
      if (ttlMs > 0 && getRequestVersion(resolvedKey) === requestVersion) {
        cachedGetResponses.set(resolvedKey, {
          data: cloneData(response.data),
          headers: response.headers,
          status: response.status,
          statusText: response.statusText,
          expiresAt: Date.now() + ttlMs
        });
      }

      return response;
    })
    .finally(() => {
      inFlightGetRequests.delete(resolvedKey);
    });

  if (dedupe) {
    inFlightGetRequests.set(resolvedKey, requestPromise);
  }

  return requestPromise;
};

api.invalidateCache = (...matchers) => {
  if (matchers.length === 0) {
    cachedGetResponses.clear();
    inFlightGetRequests.clear();
    return;
  }

  matchers.forEach((matcher) => invalidateCacheEntries(matcher));
};

export default api;
